import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";

export function useFollowedAnimalIds() {
  return useQuery<{ animalIds: string[] }>({
    queryKey: ["/api/users/me/follows/ids"],
  });
}

export function useFollowStatus(animalId: string) {
  const { data } = useFollowedAnimalIds();
  return data?.animalIds?.includes(animalId) ?? false;
}

export function useToggleFollow(animalId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      return apiRequest("POST", `/api/animals/${encodeURIComponent(animalId)}/follow`);
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ["/api/users/me/follows/ids"] });
      const previous = queryClient.getQueryData<{ animalIds: string[] }>(["/api/users/me/follows/ids"]);
      queryClient.setQueryData<{ animalIds: string[] }>(["/api/users/me/follows/ids"], (old) => {
        if (!old) return { animalIds: [animalId] };
        const isFollowing = old.animalIds.includes(animalId);
        return {
          animalIds: isFollowing
            ? old.animalIds.filter(id => id !== animalId)
            : [...old.animalIds, animalId],
        };
      });
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(["/api/users/me/follows/ids"], context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/users/me/follows/ids"] });
      queryClient.invalidateQueries({ queryKey: ["/api/users/me/followed-animals"] });
    },
  });
}
